import { comTelefoneLegivel, rotulo } from '../formato';
import { degrauDe, NIVEL_DO_RADAR } from '../situacoes';
import type { Gravidade, Pendencia } from './tipos';

/**
 * O adaptador do Radar para as peças do molde (onda 552; R-M2 da re-revisão
 * da Tarefa 28). Como o `doLaboratorio.ts` é o do LabManager: quem sabe do
 * item do Radar (`GET /comercial/radar`, `fn_radar`, 0510) é este arquivo, e
 * as peças (`CartaoDePendencia`, `tipos.ts`) continuam sem saber de área
 * nenhuma.
 *
 * Mora aqui, e não em `telas/comercial/crm.ts`, porque duas telas o usam: o
 * «Hoje» do painel do Comercial e o Início do Hub (a escada do Radar com a
 * cor da pilha). O Início não importa código do Comercial; o `crm.ts`
 * reexporta, para as telas do Comercial não mudarem.
 */

/** O que falta num item do Radar (`fn_radar`, 0510), por extenso. */
export const FALTA_NO_RADAR: { readonly [falta: string]: string } = {
  resposta_nossa: 'Falta a nossa resposta',
  retorno_do_cliente: 'Falta o retorno do cliente',
  proxima_acao: 'Falta a próxima ação',
  aprovacao_interna: 'Falta a aprovação interna',
};

/** Um item do Radar (`GET /comercial/radar`, Tarefa 19). */
export interface ItemDoRadar {
  tipo: 'conversa' | 'proposta' | 'tarefa';
  referenciaId: string;
  nivel: 'critico' | 'em_risco' | 'em_andamento';
  falta: string;
  desde: string | null;
  /** Horas úteis na conversa sem resposta e na tarefa; corridas na proposta e na conversa respondida. */
  horas: number;
  diasUteis: number | null;
  /** O tempo por extenso, pronto da API (a regra única é o `haQuanto` do `RadarService`). */
  ha: string;
  quemAge: { id: string; nome: string } | null;
  titulo: string;
  link: string;
  /** D7: a oportunidade do item (só a que a pessoa vê). */
  oportunidadeId?: string | null;
}

/** D7 (Humberto, 01/11/2026): uma linha do Radar por oportunidade, com as pendências dela dentro. */
export interface LinhaDoRadar extends ItemDoRadar {
  /** Da mais urgente (a da linha) à mais leve. */
  pendencias: ItemDoRadar[];
}

/** «Também: falta a próxima ação · vencida há 2 dias; …» — as outras pendências da linha, numa frase. */
export function outrasPendencias(l: LinhaDoRadar): string | null {
  const resto = (l.pendencias ?? []).slice(1);
  if (resto.length === 0) return null;
  return `Também: ${resto.map((p) => `${(FALTA_NO_RADAR[p.falta] ?? rotulo(p.falta)).toLowerCase()} (${TIPO_NO_RADAR[p.tipo]?.toLowerCase() ?? p.tipo}, ${tempoNoRadar(p)})`).join('; ')}.`;
}

/**
 * O tempo com o verbo do item: «sem resposta há 13 h úteis», «vencida há 4 dias úteis», «parada há 8 dias».
 * O «há…» vem pronto da API (`ha`): a regra dos dias úteis mora num lugar só.
 */
export const tempoNoRadar = (i: ItemDoRadar) => {
  const { ha } = i;
  if (i.tipo === 'proposta') return `parada ${ha}`;
  if (i.tipo === 'tarefa') return `vencida ${ha}`;
  return i.falta === 'resposta_nossa' ? `sem resposta ${ha}` : `respondida ${ha}`;
};

/** Quem age, por extenso: a pessoa, «o cliente», ou a falta de alguém dita (P-TXT-02). */
export const quemAgeNoRadar = (i: ItemDoRadar) => i.quemAge?.nome
  ?? (i.falta === 'retorno_do_cliente' ? 'o cliente' : 'ninguém ainda — falta quem cuide');

/** A cor da pilha (RN-256) para cada nível do Radar: o Início do Hub e o «Hoje» do painel do Comercial. */
export const GRAVIDADE_DO_NIVEL: { readonly [nivel: string]: Gravidade } = {
  critico: 'trava', em_risco: 'atencao', em_andamento: 'saber',
};

const TIPO_NO_RADAR: { readonly [tipo: string]: string } = { conversa: 'Conversa', proposta: 'Proposta', tarefa: 'Tarefa' };
const ACAO_NO_RADAR: { readonly [tipo: string]: string } = {
  conversa: 'Abrir a conversa', proposta: 'Abrir a proposta',
};

/**
 * Um item do Radar como cartão da pilha (`CartaoDePendencia`, RN-256, P-UI-37): o «Hoje» do painel do
 * Comercial (onda 552, T28-03). O cartão é de um registro só (sem número), fala a escada do Radar
 * (`palavra`) com a cor da pilha, e leva aonde se resolve; a regra dos níveis vai para o «i».
 */
export function pendenciaDoRadar(i: ItemDoRadar | LinhaDoRadar, explicacao?: string): Pendencia {
  const linha = 'pendencias' in i ? i : null;
  const outras = linha ? outrasPendencias(linha) : null;
  const varias = !!linha && linha.pendencias.length > 1 && !!linha.oportunidadeId;
  return {
    chave: varias ? `oportunidade:${linha!.oportunidadeId}` : `${i.tipo}:${i.referenciaId}`,
    numero: null,
    titulo: comTelefoneLegivel(i.titulo),
    consequencia: `${FALTA_NO_RADAR[i.falta] ?? rotulo(i.falta)} · ${tempoNoRadar(i)}`,
    detalhe: outras ? `Quem age: ${quemAgeNoRadar(i)} · ${outras}` : `Quem age: ${quemAgeNoRadar(i)}`,
    gravidade: GRAVIDADE_DO_NIVEL[i.nivel] ?? 'saber',
    palavra: degrauDe(NIVEL_DO_RADAR, i.nivel).texto,
    area: TIPO_NO_RADAR[i.tipo] ?? 'Outro',
    acao: { texto: varias ? 'Abrir a oportunidade'
      : ACAO_NO_RADAR[i.tipo] ?? (i.link.startsWith('/comercial/oportunidade/') ? 'Abrir a oportunidade' : 'Abrir a conversa'),
    para: i.link },
    explicacao,
  };
}
